import React, { useState, useEffect, useContext } from "react";
import styled from "styled-components";
import { SelectPicker, CheckboxGroup, useToaster } from "rsuite";
import GearIcon from "@rsuite/icons/Gear";
import { useAuth0 } from "@auth0/auth0-react";
import axiosInstance from "../../../../../appHelpers/axiosInstance";
import AdminIcon from "@rsuite/icons/Admin";
import PeoplesIcon from "@rsuite/icons/Peoples";
import PlusIcon from "@rsuite/icons/Plus";
import { timeAgo } from "../../_helpers/TimeAgo";
import { useHistory } from "react-router-dom";
import { InviteUser } from "./Modal/InviteUser";
import { Avatar } from "../../../../../appHelpers/appCommonSC";
import { UserContext } from "../../_context/UserContext";
import Card from "../../../../ui/Card/Card";
import Badge from "../../../../ui/Badge/Badge";
import Button from "../../../../ui/Button/Button";
import SectionRibbon from "../../../../ui/SectionRibbon/SectionRibbon";
import Modal from "../../../../ui/Modal/Modal";
import NoDataState from "../../../../ui/NoDataState/NoDataState";
import Loader from "../../../../ui/Loader/Loader";
import { colors } from "../../../../ui/theme/colors";

export const CustomerUserListFC = (props) => {
  const history = useHistory();
  const toaster = useToaster();
  const [users, setUsers] = useState([]);
  const { userRoles } = useContext(UserContext);
  const [removeUsers, setRemoveUsers] = useState([]);
  const [addUserValue, setAddUserValue] = useState(null);
  const [getAddUsers, setGetAddUsers] = useState([]);
  const [listAddUsers, setListAddUsers] = useState([]);
  const [basicUsers, setBasicUsers] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [loadingAddUsers, setLoadingAddUsers] = useState(true);
  const [loadingOperations, setLoadingOperations] = useState(false);
  const { getAccessTokenSilently } = useAuth0();
  const [openAddUser, setOpenAddUser] = useState(false);

  const getUserList = async () => {
    setLoadingUsers(true);
    try {
      const token = await getAccessTokenSilently({
        audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}`,
      });

      const userRes = await axiosInstance({
        url: `/customer/users/${props.customer}`,
        data: ``,
        headers: { authorization: `Bearer ${token}` },
      });

      setUsers(userRes?.data[0]);
    } catch (error) {
      console.error(error);
      toaster.push(errorMessage, "topCenter");
    }
    setLoadingUsers(false);
  };

  const getAddUserList = async () => {
    setLoadingAddUsers(true);
    try {
      const token = await getAccessTokenSilently({
        audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}`,
      });

      const userRes = await axiosInstance({
        url: `/admin/user/list`,
        data: ``,
        headers: { authorization: `Bearer ${token}` },
      });

      setGetAddUsers(userRes?.data[0]);
    } catch (error) {
      console.error(error);
      toaster.push(errorMessage, "topCenter");
    }
    setLoadingAddUsers(false);
  };

  const handleAddUserSubmit = async () => {
    setLoadingOperations(true);
    try {
      const token = await getAccessTokenSilently({
        audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}`,
      });

      await axiosInstance({
        method: "POST",
        url: `/customer/update/users/`,
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${token}`,
        },
        data: JSON.stringify({
          customer_id: props.customer,
          user: addUserValue,
          addUser: true,
        }),
      });

      await getUserList();
      setAddUserValue(null);
      setOpenAddUser(false);
      toaster.push(addUserSuccessMessage, "topCenter");
    } catch (error) {
      console.error(error);
      toaster.push(errorMessage, "topCenter");
    } finally {
      setLoadingOperations(false);
    }
  };

  const handleRemoveUserSubmit = async () => {
    setLoadingOperations(true);
    try {
      const token = await getAccessTokenSilently({
        audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}`,
      });

      await axiosInstance({
        method: "POST",
        url: `/customer/update/users/`,
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${token}`,
        },
        data: JSON.stringify({
          customer_id: props.customer,
          user: removeUsers,
          addUser: false,
        }),
      });

      setRemoveUsers([]);
      await getUserList();
      toaster.push(removeUserSuccessMessage, "topCenter");
    } catch (error) {
      console.error(error);
      toaster.push(errorMessage, "topCenter");
    } finally {
      setLoadingOperations(false);
    }
  };

  const filterUserList = () => {
    setBasicUsers(
      users.filter(
        (user) =>
          user.roles["roles"].includes("basic") &&
          !user.roles["roles"].includes("admin"),
      ),
    );
    setAdminUsers(
      users.filter((user) => user.roles["roles"].includes("admin")),
    );
  };

  const initAddUserList = () => {
    setListAddUsers(
      getAddUsers.map((user) => ({
        label: `${user.first_name} ${user.last_name} (${user.email_address})`,
        value: user.email_address,
      })),
    );
  };

  const handleUserClick = (email) => {
    history.push(`/admin/user/${email}`);
  };

  const getStatusVariant = (status) => {
    switch (status) {
      case "active":
        return "success";
      case "invited":
        return "neutral";
      default:
        return "error";
    }
  };

  useEffect(() => {
    getUserList();
    getAddUserList();
  }, []);

  useEffect(() => {
    filterUserList();
    initAddUserList();
  }, [users, getAddUsers]);

  if (loadingUsers) {
    return (
      <Container>
        <Loader content="Loading users..." centered variant="glass" />
      </Container>
    );
  }

  const addUserFooterActions = (
    <FooterButtons>
      <Button
        variant="subtle"
        onClick={() => setOpenAddUser(false)}
        disabled={loadingOperations}
      >
        Cancel
      </Button>
      <Button
        variant="primary"
        onClick={handleAddUserSubmit}
        loading={loadingOperations}
        disabled={!addUserValue}
      >
        Add User
      </Button>
    </FooterButtons>
  );

  return (
    <Container>
      <SectionRibbon
        title="Customer Users"
        icon={PeoplesIcon}
        count={`${adminUsers.length + basicUsers.length}`}
        countLabel="Total Users"
        variant="primary"
        rightContent={
          <ActionButtons>
            <InviteUser
              customerData={props.customerData}
              getUserList={getUserList}
            />
            {userRoles.includes("superuser") && (
              <Button
                variant="outline"
                size="small"
                onClick={() => setOpenAddUser(true)}
              >
                <PlusIcon style={{ marginRight: "6px", fontSize: "14px" }} />
                Add Existing User
              </Button>
            )}
            {removeUsers.length > 0 && userRoles.includes("superuser") && (
              <Button
                variant="subtle"
                size="small"
                onClick={handleRemoveUserSubmit}
                loading={loadingOperations}
              >
                Remove {removeUsers.length} User
                {removeUsers.length > 1 ? "s" : ""}
              </Button>
            )}
          </ActionButtons>
        }
      />

      <CheckboxGroup
        name="checkboxList"
        value={removeUsers}
        onChange={(value) => setRemoveUsers(value)}
      >
        {/* Admin Users Section */}
        <UsersSection>
          <SectionLabel>
            <Badge variant="primary" size="small">
              <AdminIcon style={{ marginRight: "6px", fontSize: "14px" }} />
              ADMINISTRATORS ({adminUsers?.length})
            </Badge>
          </SectionLabel>

          {adminUsers.length > 0 ? (
            <UsersGrid>
              {adminUsers.map((user, index) => (
                <UserCard
                  key={user.email_address}
                  user={user}
                  index={index}
                  onUserClick={handleUserClick}
                  showCheckbox={userRoles.includes("superuser")}
                  removeUsers={removeUsers}
                  setRemoveUsers={setRemoveUsers}
                />
              ))}
            </UsersGrid>
          ) : (
            <NoDataState
              icon="👥"
              title="No Administrators"
              message="No administrator users found for this customer."
            />
          )}
        </UsersSection>

        {/* Basic Users Section */}
        <UsersSection>
          <SectionLabel>
            <Badge variant="info" size="small">
              <PeoplesIcon style={{ marginRight: "6px", fontSize: "14px" }} />
              USERS ({basicUsers?.length})
            </Badge>
          </SectionLabel>

          {basicUsers.length > 0 ? (
            <UsersGrid>
              {basicUsers.map((user, index) => (
                <UserCard
                  key={user.email_address}
                  user={user}
                  index={index}
                  onUserClick={handleUserClick}
                  showCheckbox={true}
                  removeUsers={removeUsers}
                  setRemoveUsers={setRemoveUsers}
                />
              ))}
            </UsersGrid>
          ) : (
            <NoDataState
              icon="👤"
              title="No Users"
              message="No standard users found for this customer."
            />
          )}
        </UsersSection>
      </CheckboxGroup>

      {/* Add User Modal */}
      <Modal
        size="xs"
        open={openAddUser}
        onClose={() => setOpenAddUser(false)}
        title="Add Existing User to Customer"
        showFooter={true}
        footerActions={addUserFooterActions}
        loading={loadingOperations}
      >
        <FormContainer>
          <FormSection>
            <FormLabel>Select User</FormLabel>
            <FormHelp>Choose an existing user to add to this customer</FormHelp>
            <StyledSelectPicker
              data={listAddUsers}
              value={addUserValue}
              onChange={setAddUserValue}
              placeholder="Search and select a user..."
              block
              loading={loadingAddUsers}
              searchable
            />
          </FormSection>
        </FormContainer>
      </Modal>
    </Container>
  );
};

const UserCard = ({
  user,
  index,
  onUserClick,
  showCheckbox,
  removeUsers,
  setRemoveUsers,
}) => {
  return (
    <Card animated visible={true} delay={index * 50} hoverable>
      <UserContent>
        <UserHeader>
          <UserInfo>
            {showCheckbox && (
              <CheckboxContainer
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();

                  // Toggle the checkbox state directly
                  const isCurrentlyChecked = removeUsers.includes(
                    user.email_address,
                  );
                  let newValues;

                  if (!isCurrentlyChecked) {
                    newValues = [...removeUsers, user.email_address];
                  } else {
                    newValues = removeUsers.filter(
                      (email) => email !== user.email_address,
                    );
                  }

                  setRemoveUsers(newValues);
                }}
              >
                <UserCheckbox
                  type="checkbox"
                  value={user.email_address}
                  checked={removeUsers.includes(user.email_address)}
                  readOnly
                />
              </CheckboxContainer>
            )}
            <ClickableUserInfo onClick={() => onUserClick(user.email_address)}>
              <StyledAvatar>
                {(user.first_name ? user.first_name.charAt(0) : "") +
                  (user.last_name ? user.last_name.charAt(0) : "")}
              </StyledAvatar>
              <UserDetails>
                <UserName>
                  {user.first_name} {user.last_name}
                </UserName>
                <UserEmail>{user.email_address}</UserEmail>
              </UserDetails>
            </ClickableUserInfo>
          </UserInfo>
          <UserActions>
            <Badge variant={getStatusVariant(user.status)} size="small">
              {user.status}
            </Badge>
            <ActionButton
              onClick={(e) => {
                e.stopPropagation();
                onUserClick(user.email_address);
              }}
            >
              <GearIcon />
            </ActionButton>
          </UserActions>
        </UserHeader>

        <ClickableUserMeta onClick={() => onUserClick(user.email_address)}>
          <MetaItem>
            <MetaLabel>Role:</MetaLabel>
            <MetaValue>{Object.values(user.roles["roles"][0])}</MetaValue>
          </MetaItem>
          <MetaItem>
            <MetaLabel>Last Activity:</MetaLabel>
            <MetaValue>
              {user.last_logged_in ? timeAgo(user.last_logged_in) : "Never"}
            </MetaValue>
          </MetaItem>
        </ClickableUserMeta>
      </UserContent>
    </Card>
  );
};

const Container = styled.div`
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: flex-end;
  }
`;

const UsersSection = styled.div`
  margin-bottom: 40px;
`;

const SectionLabel = styled.div`
  margin-bottom: 16px;
  display: flex;
  align-items: center;
`;

const UsersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(450px, 1fr));
  gap: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const UserContent = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const UserHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
`;

const UserInfo = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  flex: 1;
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  cursor: pointer;
  border-radius: 4px;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: rgba(44, 90, 160, 0.1);
  }
`;

const UserCheckbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
  pointer-events: none;
`;

const ClickableUserInfo = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  flex: 1;
  cursor: pointer;
  min-width: 0;
`;

const ClickableUserMeta = styled.div`
  display: flex;
  gap: 24px;
  padding-top: 8px;
  border-top: 1px solid ${colors.neutral[200]};
  cursor: pointer;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 8px;
  }

  &:hover {
    background-color: rgba(44, 90, 160, 0.05);
    border-radius: 8px;
    padding: 8px;
    margin: 0 -8px;
  }
`;

const StyledAvatar = styled(Avatar)`
  width: 40px;
  height: 40px;
  font-size: 16px;
  flex-shrink: 0;
`;

const UserDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;
`;

const UserName = styled.h4`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: ${colors.neutral[800]};
  line-height: 1.2;
`;

const UserEmail = styled.span`
  font-size: 14px;
  color: ${colors.neutral[600]};
  word-break: break-word;
`;

const UserActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 2px solid ${colors.neutral[300]};
  background: white;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.3s ease;
  color: ${colors.neutral[600]};

  &:hover {
    border-color: ${colors.primary.main};
    color: ${colors.primary.main};
    transform: translateY(-1px);
  }

  .rs-icon {
    font-size: 14px;
  }
`;

const UserMeta = styled.div`
  display: flex;
  gap: 24px;
  padding-top: 8px;
  border-top: 1px solid ${colors.neutral[200]};

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 8px;
  }
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const MetaLabel = styled.span`
  font-size: 12px;
  color: ${colors.neutral[500]};
  font-weight: 500;
`;

const MetaValue = styled.span`
  font-size: 12px;
  color: ${colors.neutral[700]};
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px 0;
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FormLabel = styled.label`
  font-weight: 500;
  font-size: 14px;
  color: ${colors.neutral[800]};
`;

const FormHelp = styled.div`
  font-size: 12px;
  color: ${colors.neutral[600]};
  margin-bottom: 8px;
`;

const StyledSelectPicker = styled(SelectPicker)`
  .rs-picker-toggle {
    border-radius: 20px;
    border: 2px solid ${colors.neutral[200]};
    min-height: 48px;
    transition: all 0.3s ease;

    &:hover {
      border-color: ${colors.primary.main};
    }

    &.rs-picker-toggle-focused {
      border-color: ${colors.primary.main};
      box-shadow: 0 0 0 3px ${colors.primary.light};
    }
  }
`;

const FooterButtons = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  width: 100%;
`;

const getStatusVariant = (status) => {
  switch (status) {
    case "active":
      return "success";
    case "invited":
      return "neutral";
    default:
      return "error";
  }
};

const addUserSuccessMessage = {
  type: "success",
  header: "Success",
  description: "User added to customer successfully!",
};

const removeUserSuccessMessage = {
  type: "success",
  header: "Success",
  description: "Users removed from customer successfully!",
};

const errorMessage = {
  type: "error",
  header: "Error",
  description: "Something went wrong. Please try again.",
};
